/**
 * The `map` URL parameter: `zoom/lat/lng[/bearing[/pitch]]`.
 *
 * Same format Mapbox's own hash control uses, and the same the legacy app wrote,
 * so a link that already exists keeps meaning the same view. It moved from the
 * fragment to a query param for the reason the rest of this app's URL state did:
 * a fragment never reaches the server.
 */

export interface CameraPosition {
  zoom: number;
  center: [number, number];
  bearing: number;
  pitch: number;
}

/** Zoom at or above which bearing and pitch are meaningful enough to encode. */
export const TILT_ENCODING_ZOOM = 15;

/**
 * Round coordinates to the precision the zoom actually resolves.
 *
 * Ported from Mapbox's hash control: at low zoom, extra decimals describe a
 * position finer than a pixel, so they'd bloat the URL without changing the view.
 */
function coordinatePrecision(zoom: number): number {
  return Math.ceil((zoom * Math.LN2 + Math.log(512 / 360 / 0.5)) / Math.LN10);
}

/** Encode a camera position for the URL. */
export function formatCamera({
  zoom,
  center,
  bearing,
  pitch,
}: CameraPosition): string {
  const roundedZoom = Math.round(zoom * 100) / 100;
  const scale = 10 ** coordinatePrecision(roundedZoom);
  const lng = Math.round(center[0] * scale) / scale;
  const lat = Math.round(center[1] * scale) / scale;

  let value = `${roundedZoom}/${lat}/${lng}`;
  // Omitted when flat, which is the common case — no reason to carry `/0/0`.
  if (bearing || pitch) value += `/${Math.round(bearing * 10) / 10}`;
  if (pitch) value += `/${Math.round(pitch)}`;

  return value;
}

/**
 * Parse a `map` parameter, or null if it isn't a usable position.
 *
 * Tolerates `%2F` because the value used to live in the fragment, where some
 * clients percent-encode the separator.
 *
 * Bearing and pitch are dropped below `TILT_ENCODING_ZOOM`: the map disables
 * rotation there (see `useRotationGate`), so honouring them would produce a camera
 * the user then can't undo.
 */
export function parseCamera(value: string | null): CameraPosition | null {
  if (!value) return null;

  const parts = value.split(/\/|%2F/);
  if (parts.length < 3) return null;

  const numbers = parts.map(Number);
  if (numbers.some((entry) => !Number.isFinite(entry))) return null;

  const [zoom, lat, lng, bearing = 0, pitch = 0] = numbers;
  const tilted = zoom >= TILT_ENCODING_ZOOM;

  return {
    zoom,
    center: [lng, lat],
    bearing: tilted ? bearing : 0,
    pitch: tilted ? pitch : 0,
  };
}
